import type { ReactNode } from 'react';
import { Flag, X } from 'lucide-react';
import type { Priority } from '@karya/core';
import { cn } from '@/lib/cn';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/menu';

export const PRIORITY_LABEL: Record<Priority, string> = {
  1: 'Urgent',
  2: 'High',
  3: 'Normal',
  4: 'Low',
};
const PRIORITY_VAR: Record<Priority, string> = {
  1: 'var(--prio-urgent)',
  2: 'var(--prio-high)',
  3: 'var(--prio-normal)',
  4: 'var(--prio-low)',
};

export function PriorityFlag({
  priority,
  size = 16,
  className,
  showEmpty,
}: {
  priority: Priority | null;
  size?: number;
  className?: string;
  showEmpty?: boolean;
}) {
  if (!priority) {
    return showEmpty ? (
      <Flag
        className={cn('shrink-0 text-ink-3', className)}
        style={{ width: size, height: size }}
        strokeWidth={1.75}
        aria-hidden="true"
      />
    ) : null;
  }
  const c = PRIORITY_VAR[priority];
  return (
    <Flag
      className={cn('shrink-0', className)}
      style={{ width: size, height: size, color: c, fill: c }}
      strokeWidth={1.75}
      aria-label={`${PRIORITY_LABEL[priority]} priority`}
      role="img"
    />
  );
}

export function PriorityMenu({
  value,
  onChange,
  children,
  align = 'start',
}: {
  value: Priority | null;
  onChange: (p: Priority | null) => void;
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  return (
    <Menu>
      <MenuTrigger asChild>{children}</MenuTrigger>
      <MenuContent align={align} className="w-52" onClick={(e) => e.stopPropagation()}>
        {([1, 2, 3, 4] as Priority[]).map((p) => (
          <MenuItem
            key={p}
            icon={<PriorityFlag priority={p} />}
            shortcut={`Ctrl ${p}`}
            onSelect={() => onChange(p)}
            className={cn(value === p && 'bg-surface-2')}
          >
            {PRIORITY_LABEL[p]}
          </MenuItem>
        ))}
        <MenuSeparator />
        <MenuItem icon={<X className="size-4" />} shortcut="Ctrl 0" onSelect={() => onChange(null)}>
          Clear
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
